// @ts-strict-ignore
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import omitBy from 'lodash/omitBy';
import isPlainObject from 'lodash/isPlainObject';
import camelCase from 'lodash/camelCase';
import round from 'lodash/round';

import {reporter} from 'common/report/error-reporting';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {NUMERIC_DATA_PRECISION} from 'common/report/insights/util/constants';
import type {BehaviorMetricDisplay} from 'common/report/insights/util/types';

import './immer';

// TODO please remove these generic imports from callers as bandwidth is available; prefer "import {foo} from 'common/util/array';"
export {
  updateOrAddAtIndex,
  baseComparator,
  boolComparator,
  dateStringComparator,
  headerComparator,
  immutableSplice,
  insertAtIndex,
  numDateAlphaComparator,
  numericComparator,
  rangeComparator,
  removeByIndex,
  removeByValue,
  replaceByIndex,
  sorted,
  unique,
} from './array';

// TODO please remove these generic imports from callers as bandwidth is available; prefer "import {foo} from 'common/util/string';"
export {capitalize} from './string';

export const MeasureUtil = {
  measureTextWidth,
};

const IGNORED_RESULT_SERIES_KEYS = new Set([RESULTS_OVERALL_SUM_KEY]);

interface LargeNumber {
  factor: number;
  suffix: string;
}

const LARGE_NUMBERS: LargeNumber[] = [
  {
    factor: Math.pow(10, 24),
    suffix: `Sp`,
  },
  {
    factor: Math.pow(10, 21),
    suffix: `Sx`,
  },
  {
    factor: Math.pow(10, 18),
    suffix: `Qn`,
  },
  {
    factor: Math.pow(10, 15),
    suffix: `Qd`,
  },
  {
    factor: Math.pow(10, 12),
    suffix: `T`,
  },
  {
    factor: Math.pow(10, 9),
    suffix: `B`,
  },
  {
    factor: Math.pow(10, 6),
    suffix: `M`,
  },
  {
    factor: Math.pow(10, 3),
    suffix: `K`,
  },
  {
    factor: Math.pow(10, 0),
    suffix: ``,
  },
];

/**
 * Gets a property from an object ONLY if it's a direct property of the
 * object, not a property inherited from the object's prototype chain.
 *
 * Note: Named "getOwnAttr" instead of "getOwnProperty" to avoid confusing
 *       name collisions in app code referring to Mixpanel properties.
 *
 * Note: Don't use `obj.hasOwnProperty` directly; this does not account for the case
 *       where a user-provided object key (e.g. event/prop name) IS "hasOwnProperty".
 *       Also, prototype-less objects may be created via `Object.create(null)` which
 *       will not have an `obj.hasOwnProperty` method.
 *
 * @param {object} obj
 * @param {string} attr
 * @return {any} the property value if direct property of the object; null otherwise
 */
export function getOwnAttr<Type = any>(obj: Obj, attr: string): Type | null {
  if (isNil(obj)) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(obj, attr) ? obj[attr] : null;
}

/**
 * Trims trailing and leading zeroes from a number string
 *
 * @param {string} value - the number to abbreviate
 * @return {string} - formatted string without trailing or leading zeroes
 */
export function trimZeroesNumber(value: string): string {
  let [int, decimal = ``] = value.split(`.`);
  int = int.replace(/^0+/g, ``) || `0`;
  decimal = decimal.replace(/0+$/g, ``);
  decimal = decimal.length > 0 ? `.${decimal}` : ``;
  return `${int}${decimal}`;
}

// generic data-manipulation utils

/**
 * Turns a number into nicely formatted USD, e.g. 1,000 -> $1,000.00
 *
 * @param {number|string} dollarAmt - the number to turn into USD
 * @return {string} - number in USD
 */
export function numberInUSD(
  dollarAmt: number | string,
  options?: {maximumFractionDigits?: number; minimumFractionDigits?: number},
): string {
  return dollarAmt.toLocaleString(`en-US`, {currency: `USD`, style: `currency`, ...options});
}

/**
 * Turns a number into nicely formatted USD, using parentheses for negative numbers, e.g. -1,000 -> ($1,000.00)
 * Allows amounts smaller than one cent to be displayed, to 15 decimal places max
 * @param {number|string} dollarAmt - the number to turn into USD
 * @return {string} - number in USD
 */
export function numberInUSDWithParentheses(
  dollarAmt: number | string,
  options?: {maximumFractionDigits?: number; minimumFractionDigits?: number},
): string {
  const amt = Number(dollarAmt);
  if (amt < 0) {
    return `(${Math.abs(amt).toLocaleString(`en-US`, {currency: `USD`, style: `currency`, minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
  }
  return amt.toLocaleString(`en-US`, {
    currency: `USD`,
    style: `currency`,
    minimumFractionDigits: 2,
    maximumFractionDigits: 15,
    ...options,
  });
}

export function commaizeNumber(
  value: number | string,
  {
    maxPrecision = null,
    exactPrecision = false,
  }: {
    /**
     * Max digits of decimal precision for decimal numbers. Decimals will not be added to integer numbers unless
     * `exactPrecision` is true . Use `null` to not constrain the decimal precision.
     */
    maxPrecision?: number;
    /** Use `maxPrecision` digits of precision, even for integer numbers */
    exactPrecision?: boolean;
  } = {},
): string {
  switch (typeof value) {
    case `number`:
      if (isNaN(value)) {
        return value.toString();
      }
      value = Number.isFinite(maxPrecision) ? value.toFixed(maxPrecision) : value.toString();
      break;
    case `string`:
      // noop
      break;
    default:
      return value;
  }

  let neg = false;
  if (value.startsWith(`-`)) {
    neg = true;
    value = value.slice(1);
  }

  // Parse main number
  const split = value.split(`.`);
  let commaized = parseInt(split[0] || `0`, 10).toString();

  if (commaized.length) {
    commaized = commaized.split(``).reverse().join(``);
    commaized = commaized.match(/.{1,3}/g).join(`,`);
    commaized = commaized.split(``).reverse().join(``);
  }

  if (neg) {
    commaized = `-` + commaized;
  }
  if (exactPrecision && maxPrecision === 0) {
    return commaized;
  }

  const decimalPart = parseInt(split[1], 10);
  if (exactPrecision || (!isNaN(decimalPart) && decimalPart !== 0)) {
    // If they specify a max precision, we need to pad the decimal part with 0s
    if (!split[1]) {
      split[1] = `0`;
    }
    if (maxPrecision && split[1].length < maxPrecision) {
      split[1] += `0`.repeat(maxPrecision - split[1].length);
    }
    // Add decimals, if applicable
    commaized += `.` + split[1];
  }
  return commaized;
}

/**
 * Returns a number e.g 12345 as an abbreviation e.g 123K
 * @param {number|string} number
 * @param {number} precision - decimal points
 *    e.g 12345 with precision=2 returns 123.45K
 *    e.g 12300 with precision=2 returns 123.00K
 *    e.g 1.234 with precision=2 returns 1.23
 *    e.g. 1 with precision=2 returns 1 (precision does not apply for non-abbreviated integer numbers, so 1 is
 *    returned instead of 1.00)
 * @param {trimZeroes} trimZeroes trim trailing and leading zeroes after converting to @param precision.
 * @param {removeDecimalZero} removeDecimalZero remove decimal point if decimal is zero
 * @param {boolean} spaced - adds a space between suffix for readability e.g 123.45 K
 */
export function abbreviateNumber(
  value: number | string,
  {precision = 1, spaced = false, trimZeroes = false, removeDecimalZero = true} = {},
): string {
  value = parseFloat(value.toString());

  let suffix = ``;

  for (let i = 0; i < LARGE_NUMBERS.length; i++) {
    const {factor: bignum, suffix: letter} = LARGE_NUMBERS[i];

    if (Math.abs(value) >= +bignum) {
      value /= bignum;
      suffix = letter;
      break;
    }
  }

  const isNegative = value < 0;
  let fixed: string = value.toFixed(precision);
  if (trimZeroes) {
    fixed = trimZeroesNumber(fixed);
  }
  const fixedArr: string[] = fixed.split(`.`);
  let formatted = commaizeNumber(Math.abs(parseInt(fixedArr[0], 10)));

  if (fixedArr[1] && (suffix || parseInt(fixedArr[1], 10) !== 0 || !removeDecimalZero)) {
    formatted += `.` + fixedArr[1];
  }

  return (isNegative ? `-` : ``) + formatted + (suffix && spaced ? ` ` : ``) + suffix;
}

/**
 * Abbreviates number with common latin numerical words and then rounds the abbreviated number to n significant figures
 *
 * Note: numbers smaller than 1 are rounded to 3 decimal places because significant figure rounding produces unintuitive strings in these cases
 *
 * @param {number|string} value - the number to abbreviate
 * @param {number} n - number of significant figures, n > 0
 * @param {boolean} spaced - whether to add a space between the abbreviated number and the suffix
 * @param {number} decimals - number of decimal places to round to. Overrides n if provided.
 * @return {string} - locale-formatted string abbreviation of value
 */
function abbreviateNumberNSigFigs(value: number | string, n: number, spaced = false, decimals?: number): string {
  value = parseFloat(value.toString());
  const scalar = Math.abs(value);

  if (scalar < 1) {
    const rounded = trimZeroesNumber(value.toFixed(n));
    const tooSmall = parseFloat(rounded) === 0 && value !== 0;
    return tooSmall ? `≈0` : rounded;
  }
  const {factor, suffix} = LARGE_NUMBERS.find((largeNumber) => scalar >= +largeNumber.factor);
  const maybeSpace = spaced && suffix ? ` ` : ``;
  const formattedNumber =
    decimals !== undefined ? (value / factor).toFixed(decimals) : trimZeroesNumber((value / factor).toPrecision(n));
  return `${formattedNumber}${maybeSpace}${suffix}`;
}

export function ordinalizeNumber(value: number): string {
  const absNumber = Math.abs(value);
  // numbers ending in 1, 2, or 3 but not in 11, 12, or 13
  if (![11, 12, 13].includes(absNumber % 100) && [1, 2, 3].includes(absNumber % 10)) {
    switch (absNumber % 10) {
      case 1:
        return value + `st`;
      case 2:
        return value + `nd`;
      case 3:
        return value + `rd`;
      default:
        break;
    }
  }
  return value + `th`;
}

/**
 * @deprecated Use the spread operator `{...}` instead, it's functionally the same as
 * `Object.assign` when the first argument to `Object.assign` is an empty object
 *
 * e.g., `extend(this, {clauses})` --> `{...this, clauses}`, or expanded out:
 *
 * `extend(this, {clauses: clauses})` --> `{...this, clauses: clauses}`
 *
 */
export function extend(...args: Obj[]): Obj {
  return Object.assign({}, ...args);
}

export function htmlEncodeString(value: string): string {
  return String(value)
    .replace(/&/g, `&amp;`)
    .replace(/</g, `&lt;`)
    .replace(/>/g, `&gt;`)
    .replace(/"/g, `&quot;`)
    .replace(/'/g, `&#039;`)
    .replace(/\r/g, `&#013;`)
    .replace(/\n/g, `&#010;`);
}

// return object with new keys and same values
// return object with same keys and new values
export function mapValues<T, OutT>(obj: T, func: (val: T[keyof T], key: keyof T) => OutT): Obj<OutT> {
  return Object.keys(obj).reduce((ret, k) => Object.assign(ret, {[k]: func(obj[k], k as keyof T)}), {});
}

/**
 * Gets the first value of the object, but will ignore a key of $overall
 */
function nestedObjectSansOverall(obj) {
  return obj[Object.keys(obj).find((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))];
}

export function nestedObjectDepth(obj: Obj): number {
  // Ignoring $overall nodes, because, as summary nodes, those tend to be misleadingly shallow.
  return obj !== null && typeof obj === `object` ? nestedObjectDepth(nestedObjectSansOverall(obj)) + 1 : 0;
}

export function parseUrlQueryParams(urlSearch: string): Obj<string> {
  return urlSearch
    .slice(1)
    .split(`&`)
    .reduce((params, pairStr) => {
      const [k, v] = pairStr.split(`=`);
      return Object.assign(params, {[k]: v});
    }, {});
}

function getKeys(obj: Obj, depth: number, keySet: Set<string>): void {
  if (nestedObjectDepth(obj) > depth) {
    Object.keys(obj)
      .filter((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))
      .forEach((key) => getKeys(obj[key], depth, keySet));
  } else {
    Object.keys(obj)
      .filter((key) => !IGNORED_RESULT_SERIES_KEYS.has(key))
      .forEach((key) => keySet.add(key));
  }
}

export function nestedObjectKeys(obj: Obj, depth = 1): PropertyKey[] {
  const keys: Set<string> = new Set();
  getKeys(obj, depth, keys);
  return Array.from(keys);
}

export function camelCaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelCaseKeys(v));
  } else if (isPlainObject(obj)) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [camelCase(key)]: camelCaseKeys(obj[key]),
      }),
      {},
    );
  }
  return obj;
}

export function objToFormData(params: Obj): FormData {
  const formData = new FormData();
  Object.entries(params).forEach(([name, value]) => {
    if (Array.isArray(value)) {
      // FormData does not handle arrays as you'd expect:
      // formData.append('arr', [true, 74, 'John']);
      // formData.get('arr'); -> "[true, 74, 'John']" // WRONG - array converted to string
      // formData.append('arr', true);
      // formData.append('arr', 74);
      // formData.append('arr', 'John');
      // formData.getAll('arr'); -> ['true', '74', 'John'] // CORRECT - array retains type
      value.forEach((item) => formData.append(name, item));
    } else {
      formData.append(name, value);
    }
  });
  return formData;
}

export function objToQueryString(params: Obj): string {
  return Object.keys(params)
    .map((k) => [k, encodeURIComponent(params[k])].join(`=`))
    .join(`&`);
}

export function arrayToQueryString(params: Obj[]): string {
  return params
    .map((param) =>
      Object.keys(param)
        .map((k) => [`${k}[]`, encodeURIComponent(param[k])].join(`=`))
        .join(``),
    )
    .join(`&`);
}

export function sum(arr: number[]): number {
  let _sum = 0;
  for (let i = 0; i < arr.length; i++) {
    _sum += arr[i];
  }
  return _sum;
}

/**
 *  given a monotonically increasing function fn: integer => double,
 *  find the first integer i between start and end inclusive s.t. fn(i) >= 0
 *  or end if fn(end) < 0
 */
export function binarySearch(start: number, end: number, fn: (num: number) => number): number {
  if (fn(start) >= 0) {
    return start;
  }
  if (fn(end) < 0) {
    return end;
  }
  while (end - start > 1) {
    const mid = Math.floor((end + start) / 2);
    if (fn(mid) < 0) {
      start = mid;
    } else {
      end = mid;
    }
  }
  return end;
}

// lazily initialize measureContext, rather than at module top-level,
// so that running tests from node does not cause an undefined
// variable error from document
let globalMeasureContext;
function getMeasureContext(): CanvasRenderingContext2D {
  if (!globalMeasureContext) {
    const canvas = document.createElement(`canvas`);
    globalMeasureContext = canvas.getContext && canvas.getContext(`2d`);
  }
  return globalMeasureContext;
}

/**
 * Returns false if `val` is undefined, null or NaN, otherwise true
 * NOTE: Returns true for 0 and '' (empty string)
 * @param val {any}
 * @returns {boolean}
 */
export function isDefinedValue<T>(val: T): val is NonNullable<T> {
  if (typeof val === `number`) {
    return !isNaN(val);
  }
  return val !== undefined && val !== null;
}

/* istanbul ignore next */
function measureTextWidth(str: string, font: string): number {
  const measureContext = getMeasureContext();
  if (measureContext) {
    measureContext.font = font;
    return measureContext.measureText(str).width;
  }
  return null;
}

export enum RollingWindowSize {
  Minute = `minute`,
  Hour = `hour`,
  Day = `day`,
  Week = `week`,
  Month = `month`,
  Quarter = `quarter`,
}

//get the rollingcount based on unit for calculating rolling average
export function defaultRollingWindowSize(unit: RollingWindowSize): number | null {
  switch (unit) {
    case RollingWindowSize.Minute:
      return 15;
    case RollingWindowSize.Hour:
      return 12;
    case RollingWindowSize.Day:
      return 7;
    case RollingWindowSize.Week:
      return 5;
    case RollingWindowSize.Month:
      return 3;
    case RollingWindowSize.Quarter:
      return 2;
    default:
      return null;
  }
}

export function filterAttributes<T>(attrs: Obj<T>): Obj<T> {
  return omitBy(attrs, isNil);
}

export function serializeAttributes(attrs: Obj): Obj {
  return mapValues(filterAttributes(attrs), (value) => (isObject(value) ? JSON.stringify(value) : value));
}

export function parseNumber(str: string | number): number {
  if (Number.isFinite(str as number)) {
    return str as number;
  } else if (!str) {
    return null;
  }
  const num = Number(str);
  return Number.isNaN(num) ? null : num;
}

/**
 * Generates a predictable anonymous string given
 * a number, e.g. 0 -> A, 1 -> B, 26 -> BA, 98 -> DU
 * 26 ^ 4 -> BAAAA
 * @param {number} index - Index of item to stringify
 */
export function getAlphaLabelFromIndex(index): string {
  if (index < 0) return ``;

  const MIN_CHAR_CODE = `A`.charCodeAt(0);
  const MAX_ALPHA_INDEX = 26; // A-Z
  let str = ``;

  if (index === 0) {
    return `A`;
  }

  const depth = Math.floor(index / 26);
  const mod = index % MAX_ALPHA_INDEX;

  if (depth > 0) {
    str += getAlphaLabelFromIndex(depth);
  }

  return str + String.fromCharCode(MIN_CHAR_CODE + mod);
}

/** Inverse of getAlphaLabelFromIndex, e.g. A -> 0, BA -> 26 */
export function getIndexFromAlphaLabel(label: string) {
  const MIN_CHAR_CODE = `A`.charCodeAt(0);
  let index = 0;
  const exponent = label.length - 1;
  for (let i = 0; i < label.length; i++) {
    const charValue = label.charCodeAt(i) - MIN_CHAR_CODE;
    index += charValue * Math.pow(26, exponent - i);
  }

  return index;
}
/** "A. some event" -> 0, "B. foo" -> 1, "BA. bar" -> 26 */
export function getIndexFromOrdinalLabel(ordinalPrefixedLabel: string): number | null {
  const prefixMatch = ordinalPrefixedLabel.match(/^([A-Z]+\.) /);
  const indexOfDot = (prefixMatch?.[1].length ?? 0) - 1;
  if (indexOfDot === -1) {
    return null;
  }

  const label = ordinalPrefixedLabel.substring(0, indexOfDot);
  return getIndexFromAlphaLabel(label);
}

/**
 * Normalizes data group id comparison so that null, undefined and '' are equivalent.
 */
export function dataGroupIDsEqual(a: string | number, b: string | number): boolean {
  // Make sure we treat "123" and 123 as equal.
  return (!a && !b) || a + `` === b + ``;
}

// parseUri 1.2.2
// (c) Steven Levithan <stevenlevithan.com>
// MIT License
export function parseUri(str: string): Obj {
  const o = parseUri.options,
    m = o.parser[o.strictMode ? `strict` : `loose`].exec(str),
    uri = {};
  let i = 14;

  while (i--) {
    uri[o.key[i]] = m[i] || ``;
  }

  uri[o.q.name] = {};
  uri[o.key[12]].replace(o.q.parser, function (_$0, $1, $2) {
    if ($1) {
      uri[o.q.name][$1] = $2;
    }
  });

  return uri;
}

parseUri.options = {
  strictMode: false,
  key: [
    `source`,
    `protocol`,
    `authority`,
    `userInfo`,
    `user`,
    `password`,
    `host`,
    `port`,
    `relative`,
    `path`,
    `directory`,
    `file`,
    `query`,
    `anchor`,
  ],
  q: {
    name: `queryKey`,
    parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
  },
  parser: {
    strict:
      /^(?:([^:/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?))?((((?:[^?#/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
    loose:
      /^(?:(?![^:@]+:[^:@/]*@)([^:/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#/]*\.[^?#/.]+(?:[?#]|$)))*\/?)?([^?#/]*))(?:\?([^#]*))?(?:#(.*))?)/,
  },
};

export function replaceQueryParamToFragment({queryKey}: {queryKey: string | undefined}) {
  // This is a hack to get all the fragments when a newly signed up user is redirected directly to demo project/report
  // In fetch redirect, fragments are lost somehow.
  const newUrl = new URL(window.location.href);

  const selectedQueryParam = newUrl.searchParams.get(queryKey);

  newUrl.searchParams.delete(queryKey);

  newUrl.hash = selectedQueryParam;

  window.location.assign(newUrl);
}

export function formatAxisLabelWithUnit(label: string, prefix?: string, suffix?: string): string {
  return `${prefix || ``}${label}${suffix || ``}`;
}

const DEFAULT_NUM_SIG_FIGS = 3;

// The canonical number formatting/abbreviating function per the Digits Proposal
// https://www.notion.so/mpspeed/Digits-Proposal-b5bf7aa89df8470c937e6c221c379e33
// TODO use Intl.NumberFormat to simplify this and related functions
export function formatMetricNumber(
  num: number,
  {
    abbreviate: abbreviate = true,
    abbreviateThousands: abbreviateThousands = false,
    precision: precision,
  }: {abbreviate?: boolean; abbreviateThousands?: boolean; precision?: number} = {},
): string {
  if (num === undefined) {
    return NaN.toString();
  }
  // JavaScript can handle 16 or 17 digits of precision depending on the number.
  // We'll conservatively cap the number of digits we allow to 15 to avoid all
  // floating point issues.
  const MAX_DIGITS_WITH_PRECISION = 15;
  // The max number of decimal digits we allow.
  const MAX_DECIMAL_PRECISION = 8;
  const DEFAULT_MAX_DECIMAL_DIGITS = NUMERIC_DATA_PRECISION;
  const MAX_DECIMAL_DIGITS = 2;
  const exactPrecision = typeof precision === `number`;

  // GOOT: apply rounding early if precision is 0 to fix a bug in subsequent logic where e.g. 9.62 -> 1 and 0.62 -> 0
  num = precision === 0 ? Math.round(num) : exactPrecision ? num : round(num, DEFAULT_MAX_DECIMAL_DIGITS);
  const absoluteNum = Math.abs(num);

  // If abbreviate is false and there's no specified precision, we can just
  // return the number with commas added. Make sure it's rounded first so we
  // don't have to deal with floating point errors.
  if (!abbreviate && !exactPrecision) {
    return commaizeNumber(num);
  }

  // if we have a 4 digit number, just add commas and round
  if (absoluteNum < 10_000 && absoluteNum >= 1000) {
    if (abbreviate && abbreviateThousands) {
      return abbreviateNumberNSigFigs(num, DEFAULT_NUM_SIG_FIGS, false, precision);
    }
    return commaizeNumber(num, {
      maxPrecision: precision ?? 0,
      exactPrecision,
    });
  }

  // Note: this is naiive to small floating point numbers, anything below 0.0000009 may display like 9e-7, or e.g. 1.2e-7
  const [integer, decimal] = absoluteNum.toString().split(`.`);

  // For >5 digit numbers that should be abbreviated, use our number
  // abbreviation functions.
  if (abbreviate && absoluteNum >= 10_000) {
    const leftoverLength = integer.length % 3;
    // ensures there is a max of 2 decimals in the abbreviation
    if (leftoverLength === 1) {
      return abbreviateNumberNSigFigs(num, 3, false, precision);
    }
    return abbreviateNumberNSigFigs(num, 4, false, precision);
  }

  const numIntDigits = absoluteNum >= 1 ? integer.length : 0;

  // Floats with no integer component. E.g. 0.000123
  // OR abbreviated floats. In this case abbreviated just means we stick to the
  // old format of only showing 3 or 4 digits.
  if (!Number.isInteger(absoluteNum) && (integer === `0` || abbreviate)) {
    const numWithPrecision = num.toPrecision(
      Math.max(
        1,
        Math.min(
          // precision ?? MAX_DECIMAL_DIGITS,
          numIntDigits + (precision ?? MAX_DECIMAL_DIGITS),
          exactPrecision ? MAX_DECIMAL_PRECISION : DEFAULT_MAX_DECIMAL_DIGITS,
        ),
      ),
    );

    const fixed = !exactPrecision ? trimZeroesNumber(numWithPrecision) : numWithPrecision;
    return commaizeNumber(fixed, {exactPrecision, maxPrecision: precision});
  }

  // Handle floats with an integer component if we have space for decimal digits.
  // We handle these by splitting the integer and decimal components and slicing
  // the decimal portion to the correct length based on the precision and the
  // maximum number of decimal digits we can have based on the length of the
  // integer portion.
  const decimalDigitsLeft = MAX_DIGITS_WITH_PRECISION - numIntDigits;
  if (!Number.isInteger(absoluteNum) && decimalDigitsLeft > 0) {
    // We're not handling rounding correctly here because we're in a situation
    // where we have so many digits to begin with, who cares if we lose one
    // digit of precision.
    const decimalsToSlice = exactPrecision
      ? Math.min(precision, decimalDigitsLeft)
      : Math.min(decimalDigitsLeft, MAX_DECIMAL_PRECISION);
    const decimalToPrecision = decimal ? decimal.slice(0, decimalsToSlice) : ``;
    const numWithPrecision = `${integer}.${decimalToPrecision}`;

    let fixed = !exactPrecision ? trimZeroesNumber(numWithPrecision) : numWithPrecision;
    if (num < 0) {
      fixed = `-${fixed}`;
    }

    return commaizeNumber(fixed, {exactPrecision, maxPrecision: precision});
  }
  // Round here to handle large integers with a decimal component that are too
  // large which could cause a floating point here. Here we just round them to
  // the nearest integer to avoid these issues.
  return commaizeNumber(round(num), {exactPrecision, maxPrecision: precision});
}

export function formatPercent(
  decimal: number,
  {
    precision: precision = 2,
    exactPrecision: exactPrecision = false,
    abbreviate: abbreviate = false,
    showSign: showSign = true,
  }: {abbreviate?: boolean; precision?: number; exactPrecision?: boolean; showSign?: boolean} = {},
): string {
  if (isNaN(decimal) || !isFinite(decimal)) {
    return `N/A`;
  }
  let value: string | number = Math.round(decimal * Math.pow(10, precision + 2)) / Math.pow(10, precision);
  if (exactPrecision) {
    value = value.toFixed(precision);
  }
  if (abbreviate) {
    value = formatMetricNumber(Number(value), {abbreviate: true, abbreviateThousands: true});
  }
  return `${value}${showSign ? `%` : ``}`;
}

export const formatMetricNumberWithUnits = (
  value: number | string,
  display: BehaviorMetricDisplay,
  {abbreviateThousands: abbreviateThousands = false}: {abbreviateThousands?: boolean} = {},
) => {
  const prefix = display.prefix ?? ``;
  const suffix = display.suffix ?? ``;
  const abbrev = display.abbrev ?? true;
  // This means we got something like duration which is already a fully formatted
  // string and doesn't need to be passed to formatMetricNumber.
  if (typeof value === `string`) {
    return `${prefix}${value}${suffix}`;
  }

  const precision = display.precision ?? undefined;
  const formatted = formatMetricNumber(value, {precision, abbreviate: abbrev, abbreviateThousands});
  // Make sure negative sign comes before prefix
  if (formatted[0] === `-`) {
    return `-${prefix}${formatted.slice(1)}${suffix}`;
  }
  return `${prefix}${formatted}${suffix}`;
};

export function mapToSerializableObj<T, U>(map: Map<T, U>): {[prop: string]: U} {
  const obj = {};
  for (const [key, value] of map) {
    obj[key.toString()] = value;
  }

  return obj;
}

export function assert(condition: boolean, errorMessage?: string): asserts condition {
  try {
    if (!condition) {
      throw Error([`Assertion failed`, errorMessage].filter(Boolean).join(`: `));
    }
  } catch (err) {
    reporter.warn(err);
  }
}

/**
 * Convert null or undefined to undefined, otherwise return the value
 */
export function toUndefined(val: any): any | undefined {
  if (val === null || val === undefined) {
    return undefined;
  }
  return val;
}
